import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, DollarSign, CreditCard, Calculator, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

interface RevenueData {
  date: string;
  total_revenue: number;
  transaction_count: number;
  transaction_fees: number;
  platform_fees: number;
  base_amount: number;
}

interface RevenueStats {
  totalRevenue: number;
  transactionFees: number;
  platformFees: number;
  avgTransactionValue: number;
  revenueGrowth: number;
  transactionGrowth: number;
}

export function RevenueAnalytics() {
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [stats, setStats] = useState<RevenueStats>({
    totalRevenue: 0,
    transactionFees: 0,
    platformFees: 0,
    avgTransactionValue: 0,
    revenueGrowth: 0,
    transactionGrowth: 0
  });
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRevenueData();
  }, [timeRange]);

  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      const daysBack = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      // Fetch payment transactions grouped by date
      const { data: transactions, error } = await supabase
        .from('payment_transactions')
        .select(`
          created_at,
          amount_paid,
          base_amount,
          transaction_fee,
          status
        `)
        .eq('status', 'completed')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group by date and calculate metrics (only count transactions with actual payments)
      const groupedData: { [key: string]: RevenueData } = {};
      
      transactions?.forEach(transaction => {
        // Skip free credit grants (amount_paid = 0) as they're not real purchases
        if (transaction.amount_paid === 0) return;
        
        const date = transaction.created_at.split('T')[0];
        if (!groupedData[date]) {
          groupedData[date] = {
            date,
            total_revenue: 0,
            transaction_count: 0,
            transaction_fees: 0,
            platform_fees: 0,
            base_amount: 0
          };
        }
        
        groupedData[date].total_revenue += transaction.amount_paid;
        groupedData[date].base_amount += transaction.base_amount || 0;
        groupedData[date].transaction_fees += transaction.transaction_fee || 0;
        groupedData[date].transaction_count += 1;
      });

      const chartData = Object.values(groupedData);
      setRevenueData(chartData);

      // Fetch platform fees from completed sessions in the same time range
      const { data: sessionFees, error: sessionError } = await supabase
        .from('sessions')
        .select('platform_fee_amount, completed_at')
        .eq('status', 'completed')
        .gte('completed_at', startDate.toISOString());

      if (sessionError) {
        console.error('Error fetching session fees:', sessionError);
      }

      const totalPlatformFees = sessionFees?.reduce((sum, session) => 
        sum + (Number(session.platform_fee_amount) || 0), 0) || 0;

      // Calculate stats
      const currentPeriodRevenue = chartData.reduce((sum, day) => sum + day.total_revenue, 0);
      const currentPeriodTransactions = chartData.reduce((sum, day) => sum + day.transaction_count, 0);
      const currentPeriodFees = chartData.reduce((sum, day) => sum + day.transaction_fees, 0);

      // Calculate growth (compare with previous period)
      const previousStartDate = new Date();
      previousStartDate.setDate(previousStartDate.getDate() - (daysBack * 2));
      previousStartDate.setDate(previousStartDate.getDate() + daysBack);

      const { data: previousTransactions } = await supabase
        .from('payment_transactions')
        .select('amount_paid')
        .eq('status', 'completed')
        .gte('created_at', previousStartDate.toISOString())
        .lt('created_at', startDate.toISOString());

      const previousRevenue = previousTransactions?.reduce((sum, t) => sum + t.amount_paid, 0) || 0;
      const revenueGrowth = previousRevenue > 0 ? ((currentPeriodRevenue - previousRevenue) / previousRevenue) * 100 : 0;

      setStats({
        totalRevenue: currentPeriodRevenue,
        transactionFees: currentPeriodFees,
        platformFees: totalPlatformFees,
        avgTransactionValue: currentPeriodTransactions > 0 ? currentPeriodRevenue / currentPeriodTransactions : 0,
        revenueGrowth,
        transactionGrowth: 0
      });

    } catch (error) {
      console.error('Error fetching revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const formatGrowth = (growth: number) => {
    const isPositive = growth >= 0;
    return (
      <div className={`flex items-center space-x-1 ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
        {isPositive ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
        <span>{Math.abs(growth).toFixed(1)}%</span>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5" />
            <span>Revenue Analytics</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg"></div>
              ))}
            </div>
            <div className="h-64 bg-muted rounded-lg"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5" />
            <span>Revenue Analytics</span>
          </CardTitle>
          <div className="flex space-x-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  timeRange === range
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted-foreground/10'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Credit Purchases</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
                </div>
                <div className="flex flex-col items-end">
                  {formatGrowth(stats.revenueGrowth)}
                  <DollarSign className="w-8 h-8 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Transaction Fees</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.transactionFees)}</p>
                  <p className="text-xs text-muted-foreground">15% on purchases</p>
                </div>
                <CreditCard className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Platform Fees</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.platformFees)}</p>
                  <p className="text-xs text-muted-foreground">7.5% on sessions</p>
                </div>
                <Calculator className="w-8 h-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Platform Revenue</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.transactionFees + stats.platformFees)}</p>
                  <p className="text-xs text-muted-foreground">All fees combined</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Transaction</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.avgTransactionValue)}</p>
                </div>
                <Badge variant="secondary" className="text-xs">Per purchase</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Trend Chart */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Revenue Trends</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  className="text-muted-foreground text-xs"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  className="text-muted-foreground text-xs"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => formatCurrency(value)}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                  formatter={(value: number, name: string) => [
                    formatCurrency(value),
                    name === 'total_revenue' ? 'Revenue' : 'Transaction Fees'
                  ]}
                />
                <Line 
                  type="monotone" 
                  dataKey="total_revenue" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="transaction_fees" 
                  stroke="hsl(var(--secondary))" 
                  strokeWidth={2}
                  dot={{ fill: "hsl(var(--secondary))", strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}